<template>
  <ul class="esn">
    <li v-for="section in sections" :key="section.id">
      <a
        href="#"
        class="esn__link"
        :class="{
          'esn__link--active': activeSection === section.id,
          'esn__link--disabled': section.disabled,
        }"
        @click.prevent="onNavigate(section)"
      >
        <span v-if="section.done" class="esn__num esn__num--done">
          <i class="tim-icons icon-check-2"></i>
        </span>
        <span v-else-if="section.num != null" class="esn__num">{{ section.num }}</span>
        <span class="esn__text">
          <span class="esn__label">{{ section.label }}</span>
          <span v-if="section.sublabel" class="esn__sublabel">{{ section.sublabel }}</span>
        </span>
        <span v-if="section.warn" class="esn__warn" :title="section.warnText || 'Required'"></span>
      </a>
    </li>
  </ul>
</template>

<script>
/**
 * EditorSectionNav
 * Unstyled list of section links. Parent wraps it in whatever container fits.
 * Reusable across any multi-section editor.
 *
 * @prop {Array}  sections      [{ id, num, label, sublabel?, done?, disabled?, warn?, warnText? }]
 *                              sublabel: small status line under the label (wizard steps)
 *                              done: check mark replaces the number
 *                              disabled: muted, does not emit navigate
 * @prop {String} activeSection currently visible section id
 * @event navigate(id)
 */
export default {
  name: 'EditorSectionNav',
  props: {
    sections: { type: Array, required: true },
    activeSection: { type: String, default: '' },
  },
  methods: {
    onNavigate(section) {
      if (section.disabled) return;
      this.$emit('navigate', section.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.esn {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  // On mobile, becomes a horizontal pill scroll
  @media (max-width: 991px) {
    flex-direction: row;
    overflow-x: auto;
    gap: 4px;
    padding-bottom: 4px;
    margin: -4px;
    padding: 4px;
    // hide scrollbar
    &::-webkit-scrollbar { display: none; }
    scrollbar-width: none;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.12s ease, color 0.12s ease;
    white-space: nowrap;

    &:hover {
      color: #ffffff;
      background: rgba(125, 125, 125, 0.08);
      text-decoration: none;
    }
  }

  &__link--active {
    color: #e14eca;
    background: rgba(225, 78, 202, 0.1);

    .esn__num {
      color: #e14eca;
      opacity: 0.85;
    }
  }

  &__link--disabled {
    // Muted but readable: this stacks on the label's own translucency, so
    // anything below ~0.6 reads as washed out rather than locked.
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      color: rgba(255, 255, 255, 0.85);
      background: transparent;
    }
  }

  &__num {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.04em;
    flex-shrink: 0;

    &--done {
      color: #e14eca;

      i {
        font-size: 10px;
      }
    }
  }

  &__text {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__label {
    flex: 1;
  }

  &__sublabel {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    // Neutral gray (theme-shared) but bright enough to survive the
    // disabled-link opacity stacking on locked steps.
    color: #9a9a9a;
    margin-top: 1px;
  }

  &__warn {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f7b955;
    flex-shrink: 0;
  }
}
</style>

<style>
.white-content .esn__link { color: rgba(0, 0, 0, 0.85); }
.white-content .esn__link:hover {
  color: rgba(0, 0, 0, 0.95);
  background: rgba(125, 125, 125, 0.08);
}
.white-content .esn__num { color: rgba(0, 0, 0, 0.6); }
.white-content .esn__num--done { color: #e14eca; }
.white-content .esn__link--active { background: rgba(225, 78, 202, 0.08); }
.white-content .esn__link--disabled:hover {
  color: rgba(0, 0, 0, 0.85);
  background: transparent;
}
</style>
